import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { DeeplinkKeys } from '@/consts/deepLinks';
import {
  GENAI_DEEPLINK_URL,
  PICSART_APP_URL,
  PICSART_DEEPLINK_URL,
  PICSART_MINIAPP_SCHEME_URL,
  PORTFOLIO_SHARE_SCHEME_URL,
  PORTFOLIO_SHARE_WEB_URL,
} from '@/consts/urls';
import { isPortfolioApp } from '@/utils/environment/surface';
import isNativeMobile from '@/utils/platform/isNativeMobile';
import createDeeplinkUrl from '@/utils/ui/createDeeplinkUrl';

import type { PresetShareTarget } from './shareTarget';

/** Adjust campaign every preset share link is attributed to. */
const SHARE_CAMPAIGN = 'web_2_app';

/**
 * Open Graph fields for the link preview a recipient sees in WhatsApp, Telegram
 * and the like. The `af` prefix is simply what the endpoint expects.
 */
interface ShareRequestOg {
  afOgTitle?: string;
  afOgDescription?: string;
  afOgImage?: string;
}

export interface ShareRequest {
  /** Short-link generator to POST to — one per target app. */
  endpoint: string;
  data: ShareRequestOg & {
    /** Native scheme the recipient's installed app opens. */
    deeplink: string;
    /** Web fallback when the app isn't installed. */
    webUrl: string;
    campaign: string;
  };
}

/** Omitted rather than sent empty, so the service falls back to its own defaults. */
const buildOgFields = (target: PresetShareTarget): ShareRequestOg => ({
  ...(target.title && { afOgTitle: target.title }),
  ...(target.description && { afOgDescription: target.description }),
  ...(target.previewUrl && { afOgImage: target.previewUrl }),
});

const buildPortfolioRequest = (
  slug: string,
  og: ShareRequestOg
): ShareRequest => {
  if (isNativeMobile()) {
    return {
      endpoint: GENAI_DEEPLINK_URL,
      data: {
        deeplink: createDeeplinkUrl({
          baseUrl: PORTFOLIO_SHARE_SCHEME_URL,
          params: { [DeeplinkKeys.presetId]: slug },
        }),
        // Bare site by design (matches the registered link): the app's web
        // fallback drops the recipient on the feed, not on the preset.
        webUrl: PORTFOLIO_SHARE_WEB_URL,
        campaign: SHARE_CAMPAIGN,
        ...og,
      },
    };
  }

  // Desktop: the link must resolve to web, so it can't carry `genai://` — that
  // scheme is exactly what pulls the recipient into the portfolio app. And
  // since the target is now web, the preset has to ride on the URL or the
  // recipient lands on the feed with nothing shared.
  const webUrl = createDeeplinkUrl({
    baseUrl: PORTFOLIO_SHARE_WEB_URL,
    params: { [DeeplinkKeys.presetId]: slug },
  });

  return {
    endpoint: GENAI_DEEPLINK_URL,
    data: { deeplink: webUrl, webUrl, campaign: SHARE_CAMPAIGN, ...og },
  };
};

const buildPicsartRequest = (
  slug: string,
  og: ShareRequestOg
): ShareRequest => {
  // `presetId` rides along on the web URL, so a recipient who lands on web gets
  // the shared preset rather than the feed — `presetHandler` picks it up.
  const webUrl = createDeeplinkUrl({
    baseUrl: `${PICSART_APP_URL}/gen-ai/`,
    params: { [DeeplinkKeys.presetId]: slug },
  });

  return {
    endpoint: PICSART_DEEPLINK_URL,
    data: {
      // Still a short link either way — only its target changes. Shared from
      // the app, it opens the app. Shared from desktop web, it opens the web
      // page: the sharer is on web, and aiming a desktop-originated link at the
      // `picsart://` scheme sends recipients through an app-open attempt for an
      // app they may not have.
      deeplink: isNativeMobile()
        ? createDeeplinkUrl({
            baseUrl: PICSART_MINIAPP_SCHEME_URL,
            params: {
              'package-id': MINIAPP_PACKAGE_ID,
              [DeeplinkKeys.presetId]: slug,
              theme: 'dark',
            },
          })
        : webUrl,
      webUrl,
      campaign: SHARE_CAMPAIGN,
      ...og,
    },
  };
};

/**
 * Which app the link opens is fixed by the surface the share came from — a
 * Picsart-app link never resolves in the standalone GenAI app and vice versa,
 * so each surface has its own generator and its own payload shape.
 *
 * Both branches then split again on `isNativeMobile()`: a share initiated on
 * desktop targets web, because a native scheme in a desktop-originated link
 * drags the recipient into an app instead of the page.
 *
 * `createDeeplinkUrl` doesn't encode, so the slug is encoded here; every other
 * `?presetId=` producer in the app does the same.
 */
const buildShareRequest = (target: PresetShareTarget): ShareRequest => {
  const slug = encodeURIComponent(target.slug);
  const og = buildOgFields(target);

  return isPortfolioApp()
    ? buildPortfolioRequest(slug, og)
    : buildPicsartRequest(slug, og);
};

export default buildShareRequest;
